import tippy from 'tippy.js';

/**
 * Tooltip pricing table component
 *
 * @module tooltip-pricing-table
 * @version 1.0.0
 * @extends HTMLElement
**/

customElements.component = 'tooltip-pricing-table';

if (!customElements.get(customElements.component)) {

  class tooltipPricingTable extends HTMLElement {
    constructor() {
      super();

      this.baseApiUrl = "https://api.boldcommerce.com/go_pre/pricerules/0ccab2.myshopify.com";
      this.handle = this.dataset.handle;
      this.productID = parseInt(this.dataset.productId);
      this.minPackQTY = parseInt(this.dataset.minPackQty);
      this.quantityBreaks = [];

      // Elements
      this.tooltipAnchor = this.querySelector('.tooltip-anchor');

      // Set Elements and Data
      window.theme.product
        .loadProduct(this.dataset.handle, true)
        .then((product) => {
          this.product = product;
          this.checkPriceRules(product);
          if (product.variants.length > 1) {
            this.tooltipAnchor.setAttribute('disabled', '');
          }
        });
    }

    initEvents() {
      if (!this.tooltipAnchor) {
        this.dataset.ready = "false";
        return this.innerHTML = '';
      }

      this.tooltipAnchor.addEventListener('click', e => {
        const isActive = this.getAttribute('data-active') === 'true';
        this.setAttribute('data-active', !isActive);
      });
    }

    render(product) {
      this.generatePricingTable();
      this.initEvents();

      this.dataset.ready = "true";
    }

    generatePricingTable(variantID) {
      if (!this.rulesSets) {return false}
      if (this.tippyInstance) {this.tippyInstance.destroy();}

      let selectedRuleSet, currentVariant;
      if (variantID) {
        selectedRuleSet = this.rulesSets.find(ruleset => parseInt(ruleset.variantId) === variantID).rules;
        this.tooltipAnchor.removeAttribute('disabled');

      } else {
        selectedRuleSet = this.rulesSets[0].rules;
      }
      //console.log('variantID:', variantID);
      //console.log('selectedRuleSet:', selectedRuleSet);

      let unitType, individualUnitType;
      if (this.minPackQTY > 1) {
        unitType = 'Case';
        individualUnitType = 'Unit';
      } else {
        unitType = 'Unit';
        individualUnitType = 'Unit';
      }

      this.quantityBreaks = [];
      selectedRuleSet.forEach(rule => {
        rule.actions.forEach((action) => {
          if (action.type === "DISPLAY_QTY_BREAK") {
            this.quantityBreaks.push(action);
          }
        });
      });

      this.pricingBreaks = [];
      if (variantID) {
        currentVariant = this.product.variants.find(v => v.id === variantID);
      } else {
        currentVariant = this.product.variants[0];
      }
      let originalPrice = currentVariant.price;
      let originalIndividualPrice = currentVariant.price / this.minPackQTY;
      //console.log('quantityBreaks:', this.quantityBreaks);

      this.quantityBreaks.forEach((quantityBreak, quantityBreakIndex) => {
        let quantityRange;
        if (quantityBreakIndex === this.quantityBreaks.length - 1) {
          quantityRange = `${quantityBreak.qty}+`;
        } else {
          quantityRange = `${quantityBreak.qty}-${(this.quantityBreaks[quantityBreakIndex + 1].qty - 1)}`;
        }

        //console.log('this.minPackQTY:', this.minPackQTY);

        if (quantityBreakIndex === 0) {
          this.pricingBreaks.push({
            quantityRange: `1-${quantityBreak.qty - 1}`,
            price: `<span class="unit-price">${theme.money.format(originalPrice)}</span> / ${unitType}`,
            unitPrice: `<span class="individual-price">${theme.money.format(originalIndividualPrice)} / ${individualUnitType}</span>`,
            discount: false
          });

          this.pricingBreaks.push({
            quantityRange: quantityRange,
            price: `<span class="discounted-price">${theme.money.format(quantityBreak.price)}</span> / ${unitType}`,
            unitPrice: `<span class="individual-price">${theme.money.format(quantityBreak.price / this.minPackQTY)} / ${individualUnitType}</span>`,
            discount: this.getDiscountTemplate(originalPrice, quantityBreak.price)
          });
        } else {
          this.pricingBreaks.push({
            quantityRange: quantityRange,
            price: `<span class="discounted-price">${theme.money.format(quantityBreak.price)}</span> / ${unitType}`,
            unitPrice: `<span class="individual-price">${theme.money.format(quantityBreak.price / this.minPackQTY)} / ${individualUnitType}</span>`,
            discount: this.getDiscountTemplate(originalPrice, quantityBreak.price)
          });
        }
      });

      //console.log('pricingBreaks:', this.pricingBreaks);

      let tableContainer = document.createElement('div');
      tableContainer.classList.add('pricing-table-container');
      tableContainer.setAttribute('data-min-pack-qty', this.minPackQTY);

      let tableElement = document.createElement('table');
      tableElement.innerHTML = `
        <thead>
          <tr>
            <th>${unitType}(s)</th>
            <th>
              Price
            </th>
          </tr>
        </thead>
        <tbody></tbody>
      `;

      let tableBody = tableElement.querySelector('tbody');
      this.pricingBreaks.forEach((pricingBreak) => {
        let pricingBreakRow = document.createElement('tr');
        pricingBreakRow.innerHTML = `
          <td>${pricingBreak.quantityRange}</td>
          <td><div>${pricingBreak.price} ${pricingBreak.unitPrice} <span class="percent-off" data-contains="${pricingBreak.discount}">${pricingBreak.discount}</span></div></td>
        `;
        tableBody.appendChild(pricingBreakRow);
      });

      tableContainer.append(tableElement);
      this.tableContainer = tableContainer;

      if (typeof tippy === 'function') {
        this.tippyInstance = tippy(this.tooltipAnchor, {
          content: tableContainer,
          allowHTML: true,
          trigger: 'mouseenter click',
          hideOnClick: 'toggle'
        });
      }
    }

    getDiscountTemplate(mainPrice, discountedPrice) {
      const percentageOff = 100 - (discountedPrice * 100) / mainPrice;
      return `-${this.roundPercentage(percentageOff.toFixed(2))}%`;
    }

    roundPercentage(percent) {
      let rounded = Math.round(percent * 10) / 10;

      return rounded % 1 === 0 ? Math.floor(rounded) : rounded;
    }

    updatePricingTable(variantID) {
      if (!variantID) return;

      this.generatePricingTable(variantID);
      this.removeAttribute('disabled');
    }

    async checkPriceRules(product) {
      try {
        const url = `${this.baseApiUrl}/rulesets?products=${this.dataset.productId}`;
        const response = await fetch(url);
        const data = await response.json();

        if (data.rulesets.length) {
          this.rulesSets = data.rulesets.map((ruleset) => {
            return {
              variantId: ruleset.product_selection.products[0].variant_id,
              rules: ruleset.rules.filter((rule) => rule.type === "DISPLAY"),
            };
          });

          this.render(product);
        }
      } catch (error) {
        console.error("Error catching rulesets:", error);
      }
    }
  }

  customElements.define(customElements.component, tooltipPricingTable);
}
